import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, ApiClientError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useAuthConfig } from "@/lib/useAuthConfig";
import { GoogleSignInButton } from "@/components/GoogleSignInButton";
import { AuthLayout } from "@/components/AuthLayout";
import { Field } from "@/components/ui/Field";
import { Button } from "@/components/ui/Button";
import { ArrowRightIcon } from "@/lib/icons";

export function SignupPage() {
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const config = useAuthConfig();

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [tenantName, setTenantName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const auth = await api.signup({ email, password, name, tenantName });
      signIn(auth);
      navigate("/connections/new");
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : "Something went wrong");
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthLayout
      title="Create your workspace"
      subtitle="Get end-to-end visibility into your Twilio account in under a minute."
      footer={
        <>
          Already have an account?{" "}
          <Link
            to="/login"
            className="font-medium text-brand-600 transition hover:text-brand-700 dark:text-brand-300 dark:hover:text-brand-200"
          >
            <span className="inline-flex items-center gap-1">
              Sign in
              <ArrowRightIcon size={12} />
            </span>
          </Link>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <Field
          label="Workspace name"
          required
          placeholder="Acme Corp"
          autoComplete="organization"
          value={tenantName}
          onChange={(e) => setTenantName(e.target.value)}
        />

        {config?.google.enabled && (
          <>
            <GoogleSignInButton
              mode="signup"
              tenantName={tenantName || undefined}
              redirect="/connections/new"
            />
            <Divider />
          </>
        )}

        <Field
          label="Your name"
          autoComplete="name"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <Field
          label="Work email"
          type="email"
          required
          autoComplete="email"
          placeholder="you@company.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <Field
          label="Password"
          type="password"
          required
          minLength={8}
          autoComplete="new-password"
          hint="At least 8 characters."
          placeholder="••••••••"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        {error && (
          <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300">
            {error}
          </p>
        )}
        <Button type="submit" block disabled={busy}>
          {busy ? "Creating workspace…" : "Create workspace"}
        </Button>
      </form>
    </AuthLayout>
  );
}

function Divider() {
  return (
    <div className="relative my-5 text-center">
      <span className="absolute inset-x-0 top-1/2 h-px bg-zinc-200 dark:bg-zinc-800" />
      <span className="relative bg-white px-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-500 dark:bg-zinc-900/70 dark:text-zinc-500">
        or
      </span>
    </div>
  );
}
